import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDeleteRide, useRenameRide, useRide, useRidePoints } from "../hooks/useApi";
import { useUnits, formatDate, formatDistance, formatDuration, formatEnergy, formatSpeed, formatTemp } from "../lib/units";
import { StatCard } from "../components/StatCard";
import { Badge } from "../components/Badge";
import { RideMap } from "../components/RideMap";
import { RideChart } from "../components/RideChart";
import { RideScrubber } from "../components/RideScrubber";
import { StatsReadout } from "../components/StatsReadout";

export function RideDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { system } = useUnits();

  const { data: ride, isLoading: rideLoading, isError: rideError } = useRide(id);
  const { data: points, isLoading: pointsLoading } = useRidePoints(id);

  const [cursorIndex, setCursorIndex] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const rename = useRenameRide();
  const del = useDeleteRide();

  useEffect(() => {
    setCursorIndex(0);
  }, [id]);

  if (rideLoading || pointsLoading) {
    return <div className="px-6 py-10 text-center text-slate-500 dark:text-night-400">Loading ride…</div>;
  }
  if (rideError || !ride || !points) {
    return <div className="px-6 py-10 text-center text-red-600 dark:text-red-400">Ride not found.</div>;
  }

  function startEditTitle() {
    setTitleDraft(ride!.title);
    setEditingTitle(true);
  }

  function submitTitle() {
    const title = titleDraft.trim();
    setEditingTitle(false);
    if (title && title !== ride!.title) rename.mutate({ id: ride!.id, title });
  }

  async function handleDelete() {
    if (!confirm(`Delete "${ride!.title}"? This can't be undone.`)) return;
    await del.mutateAsync(ride!.id);
    navigate("/", { replace: true });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link to="/" className="mb-3 inline-block text-sm text-slate-500 hover:underline dark:text-night-400">
        ← Dashboard
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={submitTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitTitle();
                if (e.key === "Escape") setEditingTitle(false);
              }}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-2xl font-semibold dark:border-night-700 dark:bg-night-900"
            />
          ) : (
            <h1
              className="cursor-text text-2xl font-semibold tracking-tight"
              title="Click to rename"
              onClick={startEditTitle}
            >
              {ride.title}
            </h1>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-night-400">
            {formatDate(ride.startedAt)}
            {ride.startTimeSource !== "filename" && <Badge tone="warning">approximate start time</Badge>}
            {ride.faultCount > 0 && (
              <Badge tone="danger">
                {ride.faultCount} fault{ride.faultCount === 1 ? "" : "s"} ({ride.faultCodes.join(", ")})
              </Badge>
            )}
            {ride.skippedRowCount > 0 && (
              <Badge tone="warning">
                {ride.skippedRowCount} of {ride.rawRowCount} rows skipped
              </Badge>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleDelete}
          className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950"
        >
          Delete ride
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard icon="📏" label="Distance" value={formatDistance(ride.distanceMeters, system)} />
        <StatCard icon="⏱️" label="Duration" value={formatDuration(ride.durationSec)} />
        <StatCard icon="💨" label="Top speed" value={formatSpeed(ride.maxSpeedMs, system)} />
        <StatCard icon="📈" label="Avg speed" value={formatSpeed(ride.avgSpeedMs, system)} />
        <StatCard icon="⛰️" label="Elevation gain" value={formatDistance(ride.elevationGainM, system)} />
        <StatCard icon="⚡" label="Energy used" value={formatEnergy(ride.whUsed)} />
        <StatCard
          icon="🔋"
          label="Battery"
          value={ride.batteryStartPct !== null && ride.batteryEndPct !== null ? `${ride.batteryStartPct.toFixed(0)}% → ${ride.batteryEndPct.toFixed(0)}%` : "–"}
        />
        <StatCard icon="🌡️" label="Max motor temp" value={formatTemp(ride.maxTempMotor, system)} />
        <StatCard icon="🌡️" label="Max MOSFET temp" value={formatTemp(ride.maxTempMosfet, system)} />
        <StatCard icon="🔌" label="Max motor current" value={`${ride.maxCurrentMotor.toFixed(1)} A`} />
        <StatCard icon="🎚️" label="Max duty cycle" value={`${(ride.maxDutyCycle * 100).toFixed(0)}%`} />
        <StatCard icon="🧭" label="Samples" value={String(ride.pointCount)} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RideMap points={points} cursorIndex={cursorIndex} onCursorChange={setCursorIndex} />
        <RideChart points={points} cursorIndex={cursorIndex} onCursorChange={setCursorIndex} />
      </div>

      <div className="mt-4 space-y-4">
        <RideScrubber points={points} cursorIndex={cursorIndex} onCursorChange={setCursorIndex} />
        <StatsReadout point={points[cursorIndex]} />
      </div>
    </div>
  );
}
